/**
 * Number formatting + axis scale helpers.
 * Behavior is the contract from the design reference (`xa-charts.jsx`):
 *   fmt: ≥1e9 → "1.2B", ≥1e6 → "4.2M", ≥1e3 → "920K" (one decimal, ".0" stripped),
 *        else the raw number rounded to 2 decimals.
 */

export function fmt(n: number | null | undefined): string {
  // Deliberate divergence from the reference: it guards only null and would
  // render the literal string "NaN"; we treat NaN like null (en dash).
  if (n == null || Number.isNaN(n)) return '–';
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (abs >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (abs >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return String(Math.round(n * 100) / 100);
}

/**
 * Full-precision value for tooltips and legends: the exact number with thousands
 * separators, but no K/M/B abbreviation and no rounding, so hovered records and
 * legend entries show the real figure. (Axis ticks and on-chart labels keep the
 * compact `fmt`.) Null/undefined/NaN render as an en dash, matching `fmt`.
 */
export function fmtFull(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return '–';
  return n.toLocaleString('en-US', { maximumFractionDigits: 20 });
}

/**
 * "Nice" axis maximum: 1 / 2 / 2.5 / 4 / 5 / 8 × 10^n, the smallest that
 * covers `v`. Non-positive input clamps to 1.
 */
export function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const nice =
    norm <= 1 ? 1 :
    norm <= 2 ? 2 :
    norm <= 2.5 ? 2.5 :
    norm <= 4 ? 4 :
    norm <= 5 ? 5 :
    norm <= 8 ? 8 : 10;
  return nice * mag;
}

/** Evenly-spaced tick values 0 … max (inclusive), `count` intervals. */
export function ticks(max: number, count = 4): number[] {
  const t: number[] = [];
  for (let i = 0; i <= count; i++) t.push((max / count) * i);
  return t;
}
